import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { deleteField, doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useCRM } from '../../utils/CRMContext';
import { useAuth } from '../../utils/AuthContext';
import { CRMUser, UserRole, EmployeeDesignation, ExecutiveBriefingProfile, SupplierPortalRole, SupplierWorkFlag } from '../../types';
import UnifiedInviteModal from './UnifiedInviteModal';
import { parseSupplierPortalRole, supplierPortalRoleLabel, supplierWorkFlagLabel } from '../../utils/supplierWorkFlags';
import {
  Search, Pencil, Trash2, ShieldCheck, UserCircle, ToggleLeft, ToggleRight,
  X, ChevronDown, Clock, Mail, Building2, Phone, Image, Trophy, UserPlus,
  Send, Copy, CheckCircle, AlertTriangle, RefreshCw, KeyRound, Monitor,
} from 'lucide-react';
import {
  canGrantTeamChat,
  isCrmTestUser,
  isCrmWorkstationUser,
  isDesignerRole,
  isW2EmployeeTierRole,
  normalizeCrmUserDisplayRole,
} from '../../utils/userRoles';
import { groupTeamUsers } from '../../utils/teamUserListGroups';
import {
  buildTeamPendingInvites,
  filterLooseContractorApplicants,
  filterPooledContractorApplicants,
  looseContractorHiringHref,
  type TeamPendingInvite,
} from '../../utils/teamDirectoryExtras';
import { contractorFullName } from '../../utils/contractorOnboard';
import { CONTRACTOR_APPLICANT_STATUS_LABELS } from '../../types/contractorOnboard';
import type { ContractorApplicant } from '../../types/contractorOnboard';
import { deleteContractorApplicant, subscribeContractorApplicants } from '../../utils/contractorOnboardFirestore';
import { canAccessPeopleTeam } from '../../utils/employeeBenefitsAccess';
import { formatLoginIdentifier } from '../../utils/workstationLogin';
import { getSupportedTimezones } from '../../utils/userTimezone';
import ProfilePhotoCropModal from '../ui/ProfilePhotoCropModal';
import ContractorProfileEditor, { type ContractorProfileEditorHandle } from '../contractor/ContractorProfileEditor';
import GrantAchievementModal from './GrantAchievementModal';
import UserAwardGrantsList from './UserAwardGrantsList';
import TeamActivityReportModal from './TeamActivityReportModal';
import { canViewAllProjectsTimeReport } from '../../utils/projectTimeAccess';
import { crmAuthedFetch } from '../../utils/crmAuthedFetch';
import { productionCommentAccessId } from '../../utils/productionCommentAccess';
import { projectHasSilentQaViewer } from '../../utils/qaProjectAccess';
import TestUserSilentProjects from './TestUserSilentProjects';
import './UserManagement.css';

interface UserManagementProps {
  onClose?: () => void;
}

const ROLE_META: Record<string, { label: string; chip: string; color: string; bg: string }> = {
  admin:     { label: 'Admin',     chip: 'Admins', color: '#7C3AED', bg: '#F5F3FF' },
  employee:  { label: 'Employee — Full',  chip: 'Full', color: '#2C3E2F', bg: '#F0F7F1' },
  employee_shop: { label: 'Employee — Shop', chip: 'Shop', color: '#475569', bg: '#EEF2F6' },
  employee_office: { label: 'Employee — Office', chip: 'Office', color: '#475569', bg: '#E8EEF4' },
  staff:     { label: 'Field staff', chip: 'Field', color: '#64748B', bg: '#F1F5F9' },
  architect: { label: 'Architect', chip: 'Architects', color: '#7C2D12', bg: '#FFF7ED' },
  designer:  { label: 'Designer', chip: 'Designers', color: '#0369A1', bg: '#F0F9FF' },
  engineer:  { label: 'Engineer',  chip: 'Engineers', color: '#B45309', bg: '#FFF7ED' },
  supplier:  { label: 'Supplier',  chip: 'Suppliers', color: '#166534', bg: '#ECFDF5' },
  subcontractor: { label: 'Trade', chip: 'Trades', color: '#9A3412', bg: '#FFF7ED' },
  cpa:       { label: 'CPA',       chip: 'CPAs', color: '#0F766E', bg: '#F0FDFA' },
  client:    { label: 'Client',    chip: 'Clients', color: '#92400E', bg: '#FFFBEB' },
};

/** Client is leftover display-only — do not assign it on new edits. */
const ASSIGNABLE_ROLES: UserRole[] = [
  'admin',
  'employee',
  'employee_shop',
  'employee_office',
  'staff',
  'designer',
  'architect',
  'engineer',
  'supplier',
  'cpa',
];

const FUNCTIONS_URL = 'https://us-central1-lctf-projects.cloudfunctions.net';

const UserManagement: React.FC<UserManagementProps> = () => {
  const { user: authUser } = useAuth();
  const {
    crmUsers,
    cpaInvites,
    employeeInvites,
    architectInvites,
    engineerInvites,
    supplierInvites,
    updateCRMUser,
    createCpaInvite,
    revokeCpaInvite,
    revokeEmployeeInvite,
    revokeArchitectInvite,
    revokeEngineerInvite,
    revokeSupplierInvite,
    adminSetUserProfilePhoto,
    adminClearUserProfilePhoto,
    deactivateUser,
    reactivateUser,
    deleteCRMUser,
    timberProjects,
    enhancedDeals,
    updateTimberProject,
  } = useCRM();

  const [contractorApplicants, setContractorApplicants] = useState<ContractorApplicant[]>([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<CRMUser | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [photoTargetUid, setPhotoTargetUid] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [slackDirectory, setSlackDirectory] = useState<Array<{ slackId: string; name: string; email: string }>>([]);
  const [slackDirectoryLoading, setSlackDirectoryLoading] = useState(false);
  const [slackMatchBusy, setSlackMatchBusy] = useState(false);
  const [slackMatchMessage, setSlackMatchMessage] = useState<string | null>(null);
  const [slackPickerId, setSlackPickerId] = useState('');
  const photoInputRef = useRef<HTMLInputElement>(null);
  const contractorProfileRef = useRef<ContractorProfileEditorHandle>(null);
  const [editForm, setEditForm] = useState({
    displayName: '',
    role: 'client' as UserRole,
    title: '',
    company: '',
    phone: '',
    location: '',
    timezone: '',
    notes: '',
    designations: [] as EmployeeDesignation[],
    supplierDesignations: [] as SupplierWorkFlag[],
    supplierPortalRole: 'owner' as SupplierPortalRole,
    is1099Contractor: false,
    isTestUser: false,
    isWorkstation: false,
    designerHourlyRate: '',
    executiveBriefingEnabled: false,
    executiveBriefingProfile: 'coo' as ExecutiveBriefingProfile,
    executiveBriefingViewers: [] as string[],
    teamChatEnabled: false,
  });
  const [awardTargetUser, setAwardTargetUser] = useState<CRMUser | null>(null);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showWorkstationModal, setShowWorkstationModal] = useState(false);
  const [showCpaInvite, setShowCpaInvite] = useState(false);
  const [cpaInviteName, setCpaInviteName] = useState('');
  const [cpaInviteEmail, setCpaInviteEmail] = useState('');
  const [cpaInviteSending, setCpaInviteSending] = useState(false);
  const [cpaInviteResult, setCpaInviteResult] = useState<{
    success: boolean;
    message: string;
    setupLink?: string;
    email?: string;
    portalUrl?: string;
  } | null>(null);
  const [cpaResendingId, setCpaResendingId] = useState<string | null>(null);
  const [architectResendingId, setArchitectResendingId] = useState<string | null>(null);
  const [architectResendResult, setArchitectResendResult] = useState<{
    id: string;
    email: string;
    emailSent: boolean;
    message: string;
    tempPassword?: string;
    error?: string;
  } | null>(null);
  const [cpaResetLoading, setCpaResetLoading] = useState<string | null>(null);
  const [cpaResetMessage, setCpaResetMessage] = useState<{ uid: string; text: string; error?: boolean } | null>(null);

  const isAdmin = authUser?.role === 'admin';
  const canViewTeam1099Directory = canAccessPeopleTeam(authUser?.role);
  const [contractorApplicantDeletingId, setContractorApplicantDeletingId] = useState<string | null>(null);
  const crmSelf = crmUsers.find(u => u.uid === authUser?.id);
  const canOpenActivityReport = canViewAllProjectsTimeReport({
    authRole: authUser?.role,
    designations: authUser?.designations ?? crmSelf?.designations,
  });
  const timezones = useMemo(() => getSupportedTimezones(), []);

  useEffect(() => {
    return () => {
      if (cropSrc) URL.revokeObjectURL(cropSrc);
    };
  }, [cropSrc]);

  useEffect(() => {
    if (!canViewTeam1099Directory) return undefined;
    return subscribeContractorApplicants(setContractorApplicants);
  }, [canViewTeam1099Directory]);

  const openPhotoPicker = (u: CRMUser) => {
    setPhotoTargetUid(u.uid);
    photoInputRef.current?.click();
  };

  const onAdminPhotoFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !photoTargetUid) return;
    if (!file.type.startsWith('image/')) {
      window.alert('Please choose an image file.');
      setPhotoTargetUid(null);
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      window.alert('Image must be 8 MB or smaller.');
      setPhotoTargetUid(null);
      return;
    }
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(URL.createObjectURL(file));
  };

  const closePhotoCrop = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setPhotoTargetUid(null);
  };

  const saveAdminCroppedPhoto = async (file: File) => {
    if (!photoTargetUid) return;
    setPhotoBusy(true);
    try {
      await adminSetUserProfilePhoto(photoTargetUid, file);
      closePhotoCrop();
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setPhotoBusy(false);
    }
  };

  const removeUserProfilePhoto = async (uid: string) => {
    if (!window.confirm('Remove this user’s profile photo? They will see initials until a new photo is set.')) return;
    setPhotoBusy(true);
    try {
      await adminClearUserProfilePhoto(uid);
    } catch (err) {
      console.error(err);
      window.alert(err instanceof Error ? err.message : 'Could not remove photo');
    } finally {
      setPhotoBusy(false);
    }
  };

  const filtered = useMemo(() => {
    return crmUsers.filter(u => {
      const q = search.toLowerCase();
      const matchSearch = !search ||
        u.email.toLowerCase().includes(q) ||
        u.displayName.toLowerCase().includes(q) ||
        (u.company?.toLowerCase().includes(q));
      const displayRole = normalizeCrmUserDisplayRole(u);
      const matchRole =
        roleFilter === 'all'
        || displayRole === roleFilter
        || (roleFilter === 'designer' && isDesignerRole(displayRole));
      const matchStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && u.isActive) ||
        (statusFilter === 'inactive' && !u.isActive);
      return matchSearch && matchRole && matchStatus;
    }).sort((a, b) => {
      const roleOrder: Record<string, number> = {
        admin: 0,
        employee: 1,
        employee_shop: 2,
        employee_office: 2,
        staff: 3,
        architect: 3,
        designer: 3,
        engineer: 4,
        supplier: 5,
        subcontractor: 6,
        cpa: 7,
        client: 8,
      };
      const ra = roleOrder[normalizeCrmUserDisplayRole(a)] ?? 9;
      const rb = roleOrder[normalizeCrmUserDisplayRole(b)] ?? 9;
      if (ra !== rb) return ra - rb;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [crmUsers, search, roleFilter, statusFilter]);

  const teamUsers = useMemo(
    () => filtered.filter(u => !isCrmTestUser(u) && !isCrmWorkstationUser(u)),
    [filtered],
  );
  const workstationUsers = useMemo(
    () => filtered.filter(u => isCrmWorkstationUser(u)),
    [filtered],
  );
  const testUsers = useMemo(
    () => filtered.filter(u => isCrmTestUser(u) && !isCrmWorkstationUser(u)),
    [filtered],
  );

  /** QA 1099 logins live under Test Users but should still appear in the 1099 contractors group for billing setup. */
  const teamUserGroups = useMemo(
    () =>
      groupTeamUsers([
        ...teamUsers,
        ...testUsers.filter((u) => u.is1099Contractor === true),
      ]),
    [teamUsers, testUsers],
  );

  const looseContractors = useMemo(
    () => filterLooseContractorApplicants(contractorApplicants, crmUsers, search),
    [contractorApplicants, crmUsers, search],
  );

  const pooledContractors = useMemo(
    () => filterPooledContractorApplicants(contractorApplicants, crmUsers, search),
    [contractorApplicants, crmUsers, search],
  );

  const deleteTeamContractorApplicant = async (row: ContractorApplicant) => {
    const name = contractorFullName(row) || row.email;
    const label = row.status === 'pooled' ? '1099 pool' : 'onboarding';
    if (!window.confirm(`Remove ${name} from the ${label}? This deletes their crew applicant record.`)) {
      return;
    }
    setContractorApplicantDeletingId(row.id);
    try {
      await deleteContractorApplicant(row.id);
    } catch (e) {
      window.alert((e as Error).message || 'Could not delete applicant');
    } finally {
      setContractorApplicantDeletingId(null);
    }
  };

  const renderTeamContractorApplicantCard = (row: ContractorApplicant, pooled: boolean) => (
    <div key={row.id} className="um-card">
      <div className="um-card-main um-loose-card-main">
        <div className="um-avatar" style={{ background: pooled ? '#0D9488' : '#0F766E' }} aria-hidden>
          <span>
            {(row.firstName?.[0] || '?').toUpperCase()}
            {(row.lastName?.[0] || '').toUpperCase()}
          </span>
        </div>
        <div className="um-card-info">
          <div className="um-card-name-row">
            <span className="um-card-name">{contractorFullName(row)}</span>
            <span
              className="um-role-pill"
              style={{ color: pooled ? '#0D9488' : '#0F766E', background: pooled ? '#F0FDFA' : '#F0FDFA' }}
            >
              {pooled ? 'In pool' : (CONTRACTOR_APPLICANT_STATUS_LABELS[row.status] || row.status)}
            </span>
          </div>
          <div className="um-card-email">{row.email}</div>
          {row.lastJobTitle ? <div className="um-card-job">{row.lastJobTitle}</div> : null}
        </div>
        <div className="um-loose-card-actions">
          <Link to={looseContractorHiringHref(row)} className="um-action-btn um-loose-hiring-btn" onClick={e => e.stopPropagation()}>
            Open in Hiring
          </Link>
          <button
            type="button"
            className="um-action-btn um-action-danger"
            disabled={contractorApplicantDeletingId === row.id}
            onClick={() => void deleteTeamContractorApplicant(row)}
          >
            <Trash2 size={13} />
            {contractorApplicantDeletingId === row.id ? 'Removing…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );

  const pendingInvites = useMemo(
    () => buildTeamPendingInvites({
      crmUsers,
      cpaInvites,
      employeeInvites,
      architectInvites,
      engineerInvites,
      supplierInvites,
      search,
    }),
    [crmUsers, cpaInvites, employeeInvites, architectInvites, engineerInvites, supplierInvites, search],
  );

  const roleCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    crmUsers.forEach((u) => {
      if (isCrmTestUser(u) || isCrmWorkstationUser(u)) return;
      const role = normalizeCrmUserDisplayRole(u);
      counts[role] = (counts[role] || 0) + 1;
    });
    return counts;
  }, [crmUsers]);

  const loadSlackDirectory = async () => {
    if (slackDirectory.length || slackDirectoryLoading) return;
    setSlackDirectoryLoading(true);
    try {
      const res = await crmAuthedFetch(`${FUNCTIONS_URL}/teamSlackMatchApi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'listSlackDirectory' }),
      });
      const data = await res.json();
      if (data?.success && Array.isArray(data.users)) {
        setSlackDirectory(data.users);
      } else {
        setSlackMatchMessage(data?.error || 'Could not load Slack directory');
      }
    } catch (err) {
      setSlackMatchMessage(err instanceof Error ? err.message : 'Slack directory failed');
    } finally {
      setSlackDirectoryLoading(false);
    }
  };

  const applySlackMatch = async (uid: string, slackUserId: string | null, clear = false) => {
    setSlackMatchBusy(true);
    setSlackMatchMessage(null);
    try {
      const res = await crmAuthedFetch(`${FUNCTIONS_URL}/teamSlackMatchApi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'setSlackUserMatch',
          uid,
          slackUserId: slackUserId || undefined,
          clear,
        }),
      });
      const data = await res.json();
      if (!data?.success) {
        setSlackMatchMessage(data?.error || 'Slack match failed');
        return;
      }
      setSlackMatchMessage(clear ? 'Slack match cleared.' : 'Slack match saved (manual lock).');
      setEditingUser(prev =>
        prev && prev.uid === uid
          ? {
              ...prev,
              notificationPrefs: {
                ...(prev.notificationPrefs || {}),
                slackUserId: data.user?.slackUserId || undefined,
                slackUserIdManual: data.user?.slackUserIdManual === true,
              } as CRMUser['notificationPrefs'],
            }
          : prev,
      );
    } catch (err) {
      setSlackMatchMessage(err instanceof Error ? err.message : 'Slack match failed');
    } finally {
      setSlackMatchBusy(false);
    }
  };

  const rematchSlack = async (uid: string) => {
    setSlackMatchBusy(true);
    setSlackMatchMessage(null);
    try {
      const res = await crmAuthedFetch(`${FUNCTIONS_URL}/teamSlackMatchApi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'rematchSlackUser', uid }),
      });
      const data = await res.json();
      if (!data?.success) {
        setSlackMatchMessage(data?.error || 'Re-match failed');
        return;
      }
      setSlackMatchMessage(
        data.slackUserId
          ? `Re-matched to ${data.slackUserId}`
          : 'No Slack user found by email/name.',
      );
      setEditingUser(prev =>
        prev && prev.uid === uid
          ? {
              ...prev,
              notificationPrefs: {
                ...(prev.notificationPrefs || {}),
                slackUserId: data.match?.slackUserId || undefined,
                slackUserIdManual: data.match?.slackUserIdManual === true,
              } as CRMUser['notificationPrefs'],
            }
          : prev,
      );
    } catch (err) {
      setSlackMatchMessage(err instanceof Error ? err.message : 'Re-match failed');
    } finally {
      setSlackMatchBusy(false);
    }
  };

  const openEdit = (u: CRMUser) => {
    setEditingUser(u);
    setSlackMatchMessage(null);
    setSlackPickerId(u.notificationPrefs?.slackUserId || '');
    void loadSlackDirectory();
    setEditForm({
      displayName: u.displayName,
      role: normalizeCrmUserDisplayRole(u),
      title: u.title || '',
      company: u.company || '',
      phone: u.phone || '',
      location: u.location || '',
      timezone: u.timezone || '',
      notes: u.notes || '',
      designations: u.designations || [],
      supplierDesignations: u.supplierDesignations || [],
      supplierPortalRole: parseSupplierPortalRole(u.supplierPortalRole),
      is1099Contractor: u.is1099Contractor === true,
      isTestUser: u.isTestUser === true,
      isWorkstation: u.isWorkstation === true,
      designerHourlyRate: u.designerHourlyRate != null ? String(u.designerHourlyRate) : '',
      executiveBriefingEnabled: u.executiveBriefing?.enabled === true || u.execInbox?.enabled === true,
      executiveBriefingProfile: u.executiveBriefing?.profile || (u.execInbox?.enabled ? 'ceo' : 'coo'),
      executiveBriefingViewers: u.executiveBriefing?.viewers || [],
      teamChatEnabled: u.teamChatEnabled === true,
    });
    if (isAdmin) {
      void getDoc(doc(db, 'userCompensation', u.uid)).then((snap) => {
        const rate = Number(snap.data()?.designerHourlyRate);
        setEditForm((prev) => ({
          ...prev,
          designerHourlyRate: Number.isFinite(rate) && rate > 0 ? String(rate) : prev.designerHourlyRate,
        }));
      }).catch(() => undefined);
    }
  };

  const saveEdit = async () => {
    if (!editingUser) return;
    if (editForm.is1099Contractor) {
      const profileSaved = await contractorProfileRef.current?.saveIfNeeded();
      if (profileSaved === false) return;
    }
    const executiveBriefing = editForm.executiveBriefingEnabled
      ? {
          enabled: true,
          profile: editForm.executiveBriefingProfile,
          ...(editForm.executiveBriefingViewers.length
            ? { viewers: editForm.executiveBriefingViewers }
            : {}),
        }
      : editingUser.executiveBriefing
        ? { enabled: false, profile: editForm.executiveBriefingProfile as ExecutiveBriefingProfile }
        : undefined;
    const designerRate = parseFloat(editForm.designerHourlyRate);
    await updateCRMUser(editingUser.uid, {
      displayName: editForm.displayName,
      role: editForm.role,
      title: editForm.title || undefined,
      company: editForm.company || undefined,
      phone: editForm.phone || undefined,
      location: editForm.location || undefined,
      timezone: editForm.timezone || undefined,
      notes: editForm.notes || undefined,
      designations: editForm.designations.length > 0 ? editForm.designations : undefined,
      supplierDesignations: editForm.role === 'supplier' ? editForm.supplierDesignations : undefined,
      supplierPortalRole: editForm.role === 'supplier' ? editForm.supplierPortalRole : undefined,
      is1099Contractor: isW2EmployeeTierRole(editForm.role) ? false : editForm.is1099Contractor,
      isTestUser: editForm.isWorkstation ? false : editForm.isTestUser,
      isWorkstation: editForm.isWorkstation,
      ...(executiveBriefing !== undefined ? { executiveBriefing } : {}),
      ...(isAdmin ? {
        teamChatEnabled: canGrantTeamChat({
          role: editForm.role,
          isActive: editingUser.isActive,
          is1099Contractor: isW2EmployeeTierRole(editForm.role) ? false : editForm.is1099Contractor,
          isTestUser: editForm.isWorkstation ? false : editForm.isTestUser,
          isWorkstation: editForm.isWorkstation,
        }) && editForm.teamChatEnabled,
      } : {}),
    });
    if (isAdmin) {
      await setDoc(doc(db, 'userCompensation', editingUser.uid), {
        designerHourlyRate: Number.isFinite(designerRate) && designerRate > 0 ? designerRate : deleteField(),
      }, { merge: true });
      await updateDoc(doc(db, 'users', editingUser.uid), { designerHourlyRate: deleteField() });
    }
    setEditingUser(null);
  };

  const toggleStatus = async (u: CRMUser) => {
    if (u.isActive) {
      if (window.confirm(`Deactivate ${u.displayName}? They won't be able to sign in.`)) {
        await deactivateUser(u.uid);
      }
    } else {
      await reactivateUser(u.uid);
    }
  };

  const handleDelete = async (uid: string) => {
    await deleteCRMUser(uid);
    setConfirmDelete(null);
    setExpanded(null);
  };

  const fmtDate = (d: Date | undefined) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const fmtTime = (d: Date | undefined) => {
    if (!d) return '';
    return new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : name.substring(0, 2).toUpperCase();
  };

  const avatarHues = ['#2C3E2F', '#4A6FA5', '#8B5CF6', '#D97706', '#DC2626', '#0891B2', '#7C3AED', '#059669'];
  const getAvatarColor = (uid: string) => avatarHues[(uid?.charCodeAt(0) || 0) % avatarHues.length];

  const revokePendingInvite = async (inv: TeamPendingInvite) => {
    if (!window.confirm(`Revoke invite for ${inv.email}?`)) return;
    if (inv.kind === 'cpa') await revokeCpaInvite(inv.id);
    else if (inv.kind === 'employee') await revokeEmployeeInvite(inv.id);
    else if (inv.kind === 'architect') await revokeArchitectInvite(inv.id);
    else if (inv.kind === 'engineer') await revokeEngineerInvite(inv.id);
    else if (inv.kind === 'supplier') await revokeSupplierInvite(inv.id);
  };

  const resetCpaInviteForm = () => {
    setCpaInviteName('');
    setCpaInviteEmail('');
    setCpaInviteResult(null);
  };

  const sendCpaInviteRequest = async (email: string, name: string, inviteId: string) => {
    const response = await crmAuthedFetch(`${FUNCTIONS_URL}/sendCpaInvite`, {
      method: 'POST',
      body: JSON.stringify({ email, name, inviteId }),
    });
    return response.json() as Promise<{
      success?: boolean;
      method?: string;
      message?: string;
      error?: string;
      setupLink?: string;
      portalUrl?: string;
    }>;
  };

  const handleSendCpaInvite = async () => {
    if (!cpaInviteName.trim() || !cpaInviteEmail.trim()) return;
    setCpaInviteSending(true);
    setCpaInviteResult(null);
    try {
      const email = cpaInviteEmail.trim().toLowerCase();
      const name = cpaInviteName.trim();
      const inviteId = await createCpaInvite({
        email,
        name,
        invitedBy: authUser?.id || '',
        invitedByName: authUser?.name || '',
        status: 'pending',
      });
      const data = await sendCpaInviteRequest(email, name, inviteId);
      if (data.success) {
        setCpaInviteResult({
          success: true,
          message: data.method === 'email'
            ? `Invite email sent to ${email}`
            : (data.message || 'Share these credentials with the CPA.'),
          setupLink: data.setupLink,
          email,
          portalUrl: data.portalUrl || 'https://clients.lctimberframes.com',
        });
      } else {
        setCpaInviteResult({ success: false, message: data.error || 'Failed to send invite' });
      }
    } catch (err) {
      console.error('Error sending CPA invite:', err);
      setCpaInviteResult({ success: false, message: 'Network error. Check your connection.' });
    } finally {
      setCpaInviteSending(false);
    }
  };

  const handleResendArchitectInvite = async (inv: TeamPendingInvite) => {
    setArchitectResendingId(inv.id);
    setArchitectResendResult(null);
    try {
      const response = await crmAuthedFetch(`${FUNCTIONS_URL}/sendArchitectInvite`, {
        method: 'POST',
        body: JSON.stringify({
          email: inv.email,
          name: inv.name,
          inviteId: inv.id,
        }),
      });
      const data = await response.json() as {
        success?: boolean;
        method?: string;
        message?: string;
        error?: string;
        tempPassword?: string;
      };
      if (!data.success) {
        setArchitectResendResult({
          id: inv.id,
          email: inv.email,
          emailSent: false,
          message: '',
          error: data.error || 'Failed to resend invite',
        });
        return;
      }
      setArchitectResendResult({
        id: inv.id,
        email: inv.email,
        emailSent: data.method === 'email',
        message: data.method === 'email'
          ? `Invite resent to ${inv.email}`
          : (data.message || 'Share the temporary password with the architect.'),
        tempPassword: typeof data.tempPassword === 'string' ? data.tempPassword : undefined,
      });
    } catch (err) {
      console.error('Error resending architect invite:', err);
      setArchitectResendResult({
        id: inv.id,
        email: inv.email,
        emailSent: false,
        message: '',
        error: 'Network error. Check your connection.',
      });
    } finally {
      setArchitectResendingId(null);
    }
  };

  const handleResendCpaInvite = async (inviteId: string, email: string, name: string) => {
    setCpaResendingId(inviteId);
    try {
      const data = await sendCpaInviteRequest(email, name, inviteId);
      if (!data.success) {
        window.alert(data.error || 'Failed to resend invite');
      }
    } catch (err) {
      console.error('Error resending CPA invite:', err);
      window.alert('Network error. Check your connection.');
    } finally {
      setCpaResendingId(null);
    }
  };

  const handleCpaPasswordReset = async (u: CRMUser) => {
    setCpaResetLoading(u.uid);
    setCpaResetMessage(null);
    try {
      const sendReset = httpsCallable(getFunctions(undefined, 'us-central1'), 'sendPasswordResetLinkForPartner');
      const { data } = await sendReset({ targetUid: u.uid });
      const d = data as { success?: boolean; method?: string; message?: string };
      setCpaResetMessage({
        uid: u.uid,
        text: d.method === 'email'
          ? `Password reset emailed to ${u.email}`
          : (d.message || 'Password reset link created.'),
      });
    } catch (err: unknown) {
      const e = err as { message?: string };
      setCpaResetMessage({
        uid: u.uid,
        text: e?.message || 'Failed to send password reset',
        error: true,
      });
    } finally {
      setCpaResetLoading(null);
    }
  };

  const renderUserCard = (u: CRMUser) => {
            const displayRole = normalizeCrmUserDisplayRole(u);
            const meta = ROLE_META[displayRole] || ROLE_META.client;
            const isExpanded = expanded === u.uid;
            const isDeleting = confirmDelete === u.uid;

            return (
              <div
                key={u.uid}
                className={`um-card ${!u.isActive ? 'um-card-inactive' : ''} ${isExpanded ? 'um-card-expanded' : ''}`}
              >
                <div className="um-card-main" onClick={() => setExpanded(isExpanded ? null : u.uid)}>
                  <div className="um-avatar" style={{ background: u.isActive ? getAvatarColor(u.uid) : '#CBD5E1' }}>
                    {u.photoURL ? (
                      <img src={u.photoURL} alt="" />
                    ) : (
                      <span>{getInitials(u.displayName)}</span>
                    )}
                    {!u.isActive && <div className="um-avatar-inactive-dot" />}
                  </div>

                  <div className="um-card-info">
                    <div className="um-card-name-row">
                      <span className="um-card-name">{u.displayName}</span>
                      <span
                        className="um-role-pill"
                        style={{ color: meta.color, background: meta.bg }}
                      >
                        {meta.label}
                      </span>
                      {u.designations?.includes('project_manager') && (
                        <span className="um-designation-pill">PM</span>
                      )}
                      {u.designations?.includes('sales_rep') && (
                        <span className="um-designation-pill">Sales Rep</span>
                      )}
                      {u.designations?.includes('executive_assistant') && (
                        <span className="um-designation-pill">EA</span>
                      )}
                      {u.designations?.includes('cfo') && (
                        <span className="um-designation-pill">CFO</span>
                      )}
                      {u.designations?.includes('coo') && (
                        <span className="um-designation-pill">COO</span>
                      )}
                      {u.notificationPrefs?.slackUserId && (
                        <span
                          className="um-designation-pill"
                          title={
                            u.notificationPrefs.slackUserIdManual
                              ? 'Slack match (manual override)'
                              : 'Slack match (auto)'
                          }
                        >
                          Slack {u.notificationPrefs.slackUserIdManual ? 'locked' : 'matched'}
                        </span>
                      )}
                      {u.designations?.includes('office_manager') && (
                        <span className="um-designation-pill">Office Manager</span>
                      )}
                      {u.is1099Contractor && (
                        <span className="um-designation-pill um-designation-1099">1099</span>
                      )}
                      {isCrmWorkstationUser(u) && <span className="um-test-tag um-workstation-tag">Workstation</span>}
                      {isCrmTestUser(u) && <span className="um-test-tag">Test</span>}
                      {isCrmTestUser(u) && (u.role === 'architect' || u.role === 'designer' || u.role === 'engineer' || u.role === 'supplier') && (
                        <span className="um-test-tag um-qa-tag">
                          {timberProjects.filter((p) => projectHasSilentQaViewer(p, u.uid)).length} silent
                        </span>
                      )}
                      {!u.isActive && <span className="um-inactive-tag">Inactive</span>}
                    </div>
                    <span className="um-card-email">{formatLoginIdentifier(u.email)}</span>
                  </div>

                  <div className="um-card-meta">
                    <span className="um-card-date">{fmtDate(u.lastLogin)}</span>
                  </div>

                  <ChevronDown size={16} className={`um-card-chevron ${isExpanded ? 'um-chevron-up' : ''}`} />
                </div>

                {isExpanded && (
                  <div className="um-card-detail">
                    <div className="um-detail-grid">
                      <div className="um-detail-item">
                        <Mail size={13} />
                        <span>{formatLoginIdentifier(u.email)}</span>
                      </div>
                      {u.phone && (
                        <div className="um-detail-item">
                          <Phone size={13} />
                          <span>{u.phone}</span>
                        </div>
                      )}
                      {u.company && (
                        <div className="um-detail-item">
                          <Building2 size={13} />
                          <span>{u.company}</span>
                        </div>
                      )}
                      {u.title && (
                        <div className="um-detail-item">
                          <ShieldCheck size={13} />
                          <span>{u.title}</span>
                        </div>
                      )}
                      <div className="um-detail-item">
                        <Clock size={13} />
                        <span>Joined {fmtDate(u.createdAt)}</span>
                      </div>
                      <div className="um-detail-item">
                        <Clock size={13} />
                        <span>Last login {fmtDate(u.lastLogin)} {fmtTime(u.lastLogin)}</span>
                      </div>
                    </div>

                    {u.notes && <p className="um-detail-notes">{u.notes}</p>}

                    {isCrmTestUser(u) && isAdmin && (
                      <TestUserSilentProjects
                        user={u}
                        timberProjects={timberProjects}
                        enhancedDeals={enhancedDeals}
                        onToggle={async (projectId, qaViewerUids) => {
                          await updateTimberProject(projectId, { qaViewerUids });
                          const project = timberProjects.find((p) => p.id === projectId);
                          const dealId = project?.dealId?.trim();
                          if (!dealId || u.role !== 'supplier' || !qaViewerUids.includes(u.uid)) return;
                          await setDoc(
                            doc(db, 'productionCommentAccess', productionCommentAccessId(dealId)),
                            {
                              dealId,
                              hubProjectId: projectId,
                              updatedAt: serverTimestamp(),
                            },
                            { merge: true },
                          );
                        }}
                      />
                    )}

                    {isAdmin && <UserAwardGrantsList userId={u.uid} />}

                    <div className="um-detail-actions">
                      {isAdmin && (
                        <>
                          <button
                            type="button"
                            className="um-action-btn um-action-award"
                            onClick={() => setAwardTargetUser(u)}
                          >
                            <Trophy size={13} /> Award
                          </button>
                          <button
                            type="button"
                            className="um-action-btn um-action-photo"
                            onClick={() => openPhotoPicker(u)}
                            disabled={photoBusy}
                          >
                            <Image size={13} /> {u.photoURL ? 'Change photo' : 'Set photo'}
                          </button>
                          {u.photoURL && (
                            <button
                              type="button"
                              className="um-action-btn um-action-photo-remove"
                              onClick={() => removeUserProfilePhoto(u.uid)}
                              disabled={photoBusy}
                            >
                              <Trash2 size={13} /> Remove photo
                            </button>
                          )}
                        </>
                      )}
                      {displayRole === 'cpa' && (
                        <button
                          type="button"
                          className="um-action-btn"
                          disabled={cpaResetLoading === u.uid}
                          onClick={() => handleCpaPasswordReset(u)}
                        >
                          <KeyRound size={13} /> {cpaResetLoading === u.uid ? 'Sending…' : 'Reset password'}
                        </button>
                      )}
                      {cpaResetMessage?.uid === u.uid && (
                        <span className={`um-reset-note ${cpaResetMessage.error ? 'is-error' : ''}`}>
                          {cpaResetMessage.text}
                        </span>
                      )}
                      <button className="um-action-btn um-action-edit" onClick={() => openEdit(u)}>
                        <Pencil size={13} /> Edit
                      </button>
                      <button
                        className={`um-action-btn ${u.isActive ? 'um-action-deactivate' : 'um-action-activate'}`}
                        onClick={() => toggleStatus(u)}
                      >
                        {u.isActive ? <><ToggleRight size={14} /> Deactivate</> : <><ToggleLeft size={14} /> Reactivate</>}
                      </button>
                      {!isDeleting ? (
                        <button className="um-action-btn um-action-delete" onClick={() => setConfirmDelete(u.uid)}>
                          <Trash2 size={13} /> Delete
                        </button>
                      ) : (
                        <div className="um-delete-confirm">
                          <span>Permanently delete?</span>
                          <button className="um-confirm-yes" onClick={() => handleDelete(u.uid)}>Yes, delete</button>
                          <button className="um-confirm-no" onClick={() => setConfirmDelete(null)}>Cancel</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>
          );
  };

  return (
    <>
      {cropSrc && photoTargetUid && (
        <ProfilePhotoCropModal
          imageSrc={cropSrc}
          onCancel={closePhotoCrop}
          onSave={saveAdminCroppedPhoto}
        />
      )}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="um-photo-input-hidden"
        onChange={onAdminPhotoFile}
        aria-hidden
      />
    <div className="um">
      {/* Header stats */}
      <div className="um-stats" role="toolbar" aria-label="Filter by role">
        {Object.entries(ROLE_META).map(([role, meta]) => {
          const count = roleCounts[role] || 0;
          if (count === 0 && roleFilter !== role) return null;
          return (
            <button
              key={role}
              type="button"
              className={`um-stat-chip ${roleFilter === role ? 'um-stat-active' : ''}`}
              style={{ '--chip-color': meta.color, '--chip-bg': meta.bg } as React.CSSProperties}
              onClick={() => setRoleFilter(roleFilter === role ? 'all' : role as UserRole)}
            >
              <span className="um-stat-count">{count}</span>
              <span className="um-stat-label">{meta.chip}</span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="um-toolbar">
        {isAdmin && (
          <>
            <button
              type="button"
              className="ops-new-btn"
              onClick={() => setShowInviteModal(true)}
            >
              <UserPlus size={14} /> Invite teammate
            </button>
            <Link to="/settings/team/access" className="um-btn-secondary">
              <ShieldCheck size={14} /> Role access
            </Link>
          </>
        )}
        <div className="um-search">
          <Search size={15} />
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button type="button" className="um-search-clear" onClick={() => setSearch('')}>
              <X size={13} />
            </button>
          )}
        </div>
        <div className="um-filter-group">
          <div className="um-select-wrap">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <ChevronDown size={13} />
          </div>
          {canOpenActivityReport && (
            <button
              type="button"
              className="um-action-btn"
              onClick={() => setHoursOpen(true)}
              aria-label="Open activity report"
            >
              <Clock size={13} /> Activity report
            </button>
          )}
        </div>
      </div>

      {isAdmin && pendingInvites.length > 0 && (
        <div className="um-pending-invites" aria-label="Pending invites">
          <div className="um-pending-invites-head">Pending invites</div>
          {pendingInvites.map((inv) => (
            <div key={`${inv.kind}:${inv.id}`} className="um-pending-invite-block">
              <div className="um-pending-invite">
                <div className="um-pending-invite-info">
                  <strong>{inv.name}</strong>
                  <span>{inv.email} · {inv.label}</span>
                </div>
                <div className="um-pending-invite-actions">
                  {(inv.kind === 'cpa' || inv.kind === 'architect') && (
                    <button
                      type="button"
                      className="um-action-btn"
                      disabled={inv.kind === 'cpa' ? cpaResendingId === inv.id : architectResendingId === inv.id}
                      onClick={() => {
                        if (inv.kind === 'cpa') void handleResendCpaInvite(inv.id, inv.email, inv.name);
                        else void handleResendArchitectInvite(inv);
                      }}
                    >
                      <RefreshCw size={13} /> {(inv.kind === 'cpa' ? cpaResendingId : architectResendingId) === inv.id ? 'Sending…' : 'Resend'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="um-action-btn um-action-deactivate"
                    onClick={() => void revokePendingInvite(inv)}
                  >
                    Revoke
                  </button>
                </div>
              </div>
              {architectResendResult?.id === inv.id && (
                <div className={`um-pending-resend ${architectResendResult.error ? 'is-error' : ''}`}>
                  <p>{architectResendResult.error || architectResendResult.message}</p>
                  {architectResendResult.tempPassword && (
                    <p>
                      Temporary password: <code>{architectResendResult.tempPassword}</code>
                      <button
                        type="button"
                        className="um-action-btn"
                        onClick={() => {
                          void navigator.clipboard.writeText(
                            `Email: ${architectResendResult.email}\nTemporary password: ${architectResendResult.tempPassword}`,
                          );
                        }}
                      >
                        <Copy size={12} /> Copy
                      </button>
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* User list — team vs test accounts */}
      <div className="um-list">
        {([
          {
            id: 'team',
            title: 'Team',
            hint: null as string | null,
            users: teamUsers,
          },
          {
            id: 'workstation',
            title: 'Workstations',
            hint: 'Shared machine logins (Job Trailer, Cadwork, shop floor). Not a person — they stay off the org chart and project team.',
            users: workstationUsers,
          },
          {
            id: 'test',
            title: 'Test Users',
            hint: 'QA logins. Expand an architect, engineer, or supplier to grant silent project access — they will not appear on the project.',
            users: testUsers,
          },
        ]).map(section => (
          <div
            key={section.id}
            className={`um-list-section${section.id === 'test' || section.id === 'workstation' ? ' um-list-section-test' : ''}`}
          >
            <div className="um-section-head">
              <span>{section.title}</span>
              <div className="um-section-head-right">
                {section.id === 'workstation' && isAdmin && (
                  <button
                    type="button"
                    className="um-section-action-btn"
                    onClick={() => setShowWorkstationModal(true)}
                  >
                    <Monitor size={13} /> Add workstation
                  </button>
                )}
                <span className="um-section-count">{section.users.length}</span>
              </div>
            </div>
            {section.hint && <p className="um-section-hint">{section.hint}</p>}
            {section.users.length === 0 ? (
              <div className="um-empty">
                <UserCircle size={36} strokeWidth={1.2} />
                <p>
                  {section.id === 'test'
                    ? 'No test users yet.'
                    : section.id === 'workstation'
                      ? 'No workstations yet. Add Job Trailer, Cadwork PC, or Shop Floor with Add workstation.'
                      : 'No users match your filters'}
                </p>
              </div>
            ) : section.id === 'team' ? (
              teamUserGroups.map((group) => (
                <div key={group.id} className="um-role-group">
                  <div className="um-role-group-head">
                    <span>{group.label}</span>
                    <span className="um-role-group-count">{group.users.length}</span>
                  </div>
                  {group.users.map((u) => renderUserCard(u))}
                </div>
              ))
            ) : (
              section.users.map((u) => renderUserCard(u))
            )}
          </div>
        ))}

        {canViewTeam1099Directory && (
          <>
            <div className="um-list-section">
              <div className="um-section-head">
                <span>1099 pool</span>
                <div className="um-section-head-right">
                  <span className="um-section-count">{pooledContractors.length}</span>
                </div>
              </div>
              <p className="um-section-hint">
                Signed crew who are in the reusable 1099 pool but do not have a CRM login yet.
              </p>
              {pooledContractors.length === 0 ? (
                <div className="um-empty">
                  <UserCircle size={36} strokeWidth={1.2} />
                  <p>No one in the 1099 pool without a roster account.</p>
                </div>
              ) : (
                pooledContractors.map(row => renderTeamContractorApplicantCard(row, true))
              )}
            </div>

            <div className="um-list-section">
              <div className="um-section-head">
                <span>Loose 1099s</span>
                <div className="um-section-head-right">
                  <span className="um-section-count">{looseContractors.length}</span>
                </div>
              </div>
              <p className="um-section-hint">
                Crew applicants still in W-9 / agreement onboarding (not in the pool yet).
              </p>
              {looseContractors.length === 0 ? (
                <div className="um-empty">
                  <UserCircle size={36} strokeWidth={1.2} />
                  <p>No loose 1099 applicants.</p>
                </div>
              ) : (
                looseContractors.map(row => renderTeamContractorApplicantCard(row, false))
              )}
            </div>
          </>
        )}
      </div>

      <div className="um-footer-count">
        Showing {teamUsers.length} team, {workstationUsers.length} workstation, {testUsers.length} test
        {canViewTeam1099Directory
          ? `, ${pooledContractors.length} in 1099 pool, ${looseContractors.length} loose 1099`
          : ''}
        {isAdmin ? `, and ${pendingInvites.length} pending invite` : ''} of {crmUsers.length} users
      </div>

      {/* Edit modal */}
      {editingUser && (
        <div className="um-modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="um-modal" onClick={e => e.stopPropagation()}>
            <div className="um-modal-header">
              <h3>Edit User</h3>
              <button className="um-modal-close" onClick={() => setEditingUser(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="um-modal-body">
              <div className="um-field">
                <label>Name</label>
                <input
                  type="text"
                  value={editForm.displayName}
                  onChange={e => setEditForm({ ...editForm, displayName: e.target.value })}
                />
              </div>
              <div className="um-field">
                <label>Email</label>
                <input type="text" value={editingUser.email} disabled className="um-field-disabled" />
              </div>
              <div className="um-field">
                <label>Slack match (crew bot)</label>
                <p className="um-hours-help" style={{ margin: '0 0 8px' }}>
                  Auto-matches by email (then name). Manual override locks the mapping so auto-match cannot overwrite it.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <code style={{ fontSize: 12 }}>
                    {editingUser.notificationPrefs?.slackUserId || 'Not matched'}
                    {editingUser.notificationPrefs?.slackUserIdManual ? ' (manual)' : editingUser.notificationPrefs?.slackUserId ? ' (auto)' : ''}
                  </code>
                  <select
                    value={slackPickerId}
                    onChange={e => setSlackPickerId(e.target.value)}
                    disabled={slackDirectoryLoading || slackMatchBusy}
                    style={{ minWidth: 220, maxWidth: '100%' }}
                  >
                    <option value="">
                      {slackDirectoryLoading ? 'Loading Slack users…' : 'Override — pick Slack user'}
                    </option>
                    {slackDirectory.map(s => (
                      <option key={s.slackId} value={s.slackId}>
                        {s.name}{s.email ? ` (${s.email})` : ''} — {s.slackId}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="um-btn-save"
                    disabled={slackMatchBusy || !slackPickerId}
                    onClick={() => applySlackMatch(editingUser.uid, slackPickerId)}
                  >
                    Save override
                  </button>
                  <button
                    type="button"
                    className="um-action-btn"
                    disabled={slackMatchBusy}
                    onClick={() => rematchSlack(editingUser.uid)}
                  >
                    <RefreshCw size={13} /> Re-match
                  </button>
                  <button
                    type="button"
                    className="um-action-btn"
                    disabled={slackMatchBusy || !editingUser.notificationPrefs?.slackUserId}
                    onClick={() => applySlackMatch(editingUser.uid, null, true)}
                  >
                    Clear
                  </button>
                </div>
                {slackMatchMessage && (
                  <p className="um-hours-help" style={{ marginTop: 8 }}>{slackMatchMessage}</p>
                )}
              </div>
              <div className="um-field">
                <label className="um-test-toggle">
                  <input
                    type="checkbox"
                    checked={editForm.isTestUser}
                    onChange={e => setEditForm({ ...editForm, isTestUser: e.target.checked })}
                  />
                  Test user — show under Test Users (QA / cutover)
                </label>
              </div>
              <div className="um-field">
                <label className="um-test-toggle">
                  <input
                    type="checkbox"
                    checked={editForm.isWorkstation}
                    onChange={e => setEditForm({
                      ...editForm,
                      isWorkstation: e.target.checked,
                      isTestUser: e.target.checked ? false : editForm.isTestUser,
                    })}
                  />
                  Workstation — shared machine login, not a person
                </label>
              </div>
              <div className="um-field">
                <label>Role</label>
                <p className="um-hours-help" style={{ margin: '0 0 10px' }}>
                  Role controls CRM menus and settings (Leads, Pipeline, Project Hub, etc.). Pay type (1099 vs W-2) is separate below.
                </p>
                <div className="um-role-selector">
                  {ASSIGNABLE_ROLES.map((role) => {
                    const meta = ROLE_META[role];
                    return (
                    <button
                      key={role}
                      type="button"
                      className={`um-role-option ${editForm.role === role ? 'um-role-selected' : ''}`}
                      style={{ '--opt-color': meta.color, '--opt-bg': meta.bg } as React.CSSProperties}
                      onClick={() => {
                        const next1099 = isW2EmployeeTierRole(role) ? false : editForm.is1099Contractor;
                        setEditForm({ ...editForm, role, is1099Contractor: next1099 });
                      }}
                    >
                      {meta.label}
                    </button>
                    );
                  })}
                </div>
                {editForm.role === 'cpa' && (
                  <p className="um-hours-help" style={{ marginTop: 8 }}>
                    CPA / Bookkeeper — Finances only. They can comment, but cannot change numbers.
                  </p>
                )}
              </div>
              {editForm.role === 'supplier' && (
                <>
                  <div className="um-field">
                    <label>Access</label>
                    <p className="um-hours-help" style={{ margin: '0 0 8px' }}>
                      Owner sees quotes and totals. Employee can use files, comments, and production — not prices or invoices.
                    </p>
                    <div className="um-designation-toggles">
                      {(['owner', 'employee'] as SupplierPortalRole[]).map((level) => {
                        const active = editForm.supplierPortalRole === level;
                        return (
                          <button
                            key={level}
                            type="button"
                            className={`um-designation-btn ${active ? 'um-designation-active' : ''}`}
                            onClick={() => setEditForm({ ...editForm, supplierPortalRole: level })}
                          >
                            {supplierPortalRoleLabel(level)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="um-field">
                    <label>Work</label>
                    <p className="um-hours-help" style={{ margin: '0 0 8px' }}>
                      Controls Production comments after award. Timber-only users stay out of CNC-awarded jobs.
                    </p>
                    <div className="um-designation-toggles">
                      {(['timber_supplier', 'cnc_supplier'] as SupplierWorkFlag[]).map((flag) => {
                        const active = editForm.supplierDesignations.includes(flag);
                        return (
                          <button
                            key={flag}
                            type="button"
                            className={`um-designation-btn ${active ? 'um-designation-active' : ''}`}
                            onClick={() => {
                              const next = active
                                ? editForm.supplierDesignations.filter(x => x !== flag)
                                : [...editForm.supplierDesignations, flag];
                              setEditForm({ ...editForm, supplierDesignations: next });
                            }}
                          >
                            {supplierWorkFlagLabel(flag)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
              {(editForm.role === 'employee'
                || editForm.role === 'admin'
                || editForm.role === 'employee_office'
                || editForm.role === 'employee_shop'
                || editForm.role === 'staff') && (
                <div className="um-field">
                  <label>Designations</label>
                  <div className="um-designation-toggles">
                    {([
                      { key: 'project_manager' as EmployeeDesignation, label: 'Project Manager' },
                      { key: 'sales_rep' as EmployeeDesignation, label: 'Sales Rep' },
                      { key: 'estimator' as EmployeeDesignation, label: 'Estimator' },
                      { key: 'executive_assistant' as EmployeeDesignation, label: 'Executive Assistant' },
                      { key: 'cfo' as EmployeeDesignation, label: 'CFO' },
                      { key: 'coo' as EmployeeDesignation, label: 'COO' },
                      { key: 'office_manager' as EmployeeDesignation, label: 'Office Manager' },
                    ]).map(d => {
                      const active = editForm.designations.includes(d.key);
                      return (
                        <button
                          key={d.key}
                          type="button"
                          className={`um-designation-btn ${active ? 'um-designation-active' : ''}`}
                          onClick={() => {
                            const next = active
                              ? editForm.designations.filter(x => x !== d.key)
                              : [...editForm.designations, d.key];
                            setEditForm({ ...editForm, designations: next });
                          }}
                        >
                          {active ? <ShieldCheck size={13} /> : null}
                          {d.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {(editForm.role === 'employee' || editForm.role === 'admin') && (
                <div className="um-field">
                  <label>Executive day briefing</label>
                  <button
                    type="button"
                    className={`um-toggle-row ${editForm.executiveBriefingEnabled ? 'um-toggle-on' : ''}`}
                    onClick={() =>
                      setEditForm({ ...editForm, executiveBriefingEnabled: !editForm.executiveBriefingEnabled })
                    }
                  >
                    {editForm.executiveBriefingEnabled ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                    <span>
                      {editForm.executiveBriefingEnabled
                        ? 'Executive briefing enabled (logo → Day Briefing)'
                        : 'No executive briefing'}
                    </span>
                  </button>
                  {editForm.executiveBriefingEnabled && (
                    <div className="um-field" style={{ marginTop: 10 }}>
                      <label>Profile</label>
                      <div className="um-role-selector">
                        {(['ceo', 'coo'] as ExecutiveBriefingProfile[]).map(p => (
                          <button
                            key={p}
                            type="button"
                            className={`um-role-option ${editForm.executiveBriefingProfile === p ? 'um-role-selected' : ''}`}
                            onClick={() => setEditForm({ ...editForm, executiveBriefingProfile: p })}
                          >
                            {p === 'coo' ? 'COO (ops / production)' : 'CEO (full suite)'}
                          </button>
                        ))}
                      </div>
                      <label style={{ marginTop: 10 }}>Briefing viewers</label>
                      <p className="um-field-help" style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--pm-text-muted)' }}>
                        Staff who can read this executive&apos;s day briefing (admins always can).
                      </p>
                      <div className="um-designation-toggles">
                        {crmUsers
                          .filter(u => u.uid !== editingUser.uid && (u.role === 'admin' || u.role === 'employee'))
                          .map(u => {
                            const active = editForm.executiveBriefingViewers.includes(u.uid);
                            return (
                              <button
                                key={u.uid}
                                type="button"
                                className={`um-designation-btn ${active ? 'um-designation-active' : ''}`}
                                onClick={() => {
                                  const next = active
                                    ? editForm.executiveBriefingViewers.filter(x => x !== u.uid)
                                    : [...editForm.executiveBriefingViewers, u.uid];
                                  setEditForm({ ...editForm, executiveBriefingViewers: next });
                                }}
                              >
                                {u.displayName || u.email}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="um-field">
                <label>1099 contractor</label>
                {isW2EmployeeTierRole(editForm.role) && (
                  <p className="um-hours-help" style={{ margin: '0 0 8px' }}>
                    W-2 employee roles cannot be 1099 contractors — switch to Field staff or another role first.
                  </p>
                )}
                <button
                  type="button"
                  className={`um-toggle-row ${editForm.is1099Contractor ? 'um-toggle-on' : ''}`}
                  disabled={isW2EmployeeTierRole(editForm.role)}
                  onClick={() =>
                    setEditForm({ ...editForm, is1099Contractor: !editForm.is1099Contractor })
                  }
                >
                  {editForm.is1099Contractor ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                  <span>
                    {editForm.is1099Contractor
                      ? '1099 — hours via Contractor Timesheets (not W-2 payroll tracking)'
                      : 'W-2 — internal employee; not on contractor timesheets'}
                  </span>
                </button>
              </div>
              {isAdmin && canGrantTeamChat({
                role: editForm.role,
                isActive: editingUser?.isActive !== false,
                is1099Contractor: isW2EmployeeTierRole(editForm.role) ? false : editForm.is1099Contractor,
                isTestUser: editForm.isWorkstation ? false : editForm.isTestUser,
                isWorkstation: editForm.isWorkstation,
              }) && (
                <div className="um-field">
                  <label>Team chat</label>
                  <button
                    type="button"
                    className={`um-toggle-row ${editForm.teamChatEnabled ? 'um-toggle-on' : ''}`}
                    onClick={() => setEditForm({ ...editForm, teamChatEnabled: !editForm.teamChatEnabled })}
                  >
                    {editForm.teamChatEnabled ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                    <span>
                      {editForm.teamChatEnabled
                        ? 'Can use team chat'
                        : 'No team chat'}
                    </span>
                  </button>
                </div>
              )}
              {editForm.is1099Contractor && editingUser && (
                <ContractorProfileEditor
                  ref={contractorProfileRef}
                  userId={editingUser.uid}
                  displayName={editForm.displayName || editingUser.displayName}
                />
              )}
              <div className="um-field-row">
                <div className="um-field">
                  <label>Title</label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                    placeholder="Job title"
                  />
                </div>
                {isAdmin && (editForm.role === 'architect' || editForm.role === 'designer' || editForm.designerHourlyRate) && (
                  <div className="um-field">
                    <label>Designer $/hr</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editForm.designerHourlyRate}
                      onChange={e => setEditForm({ ...editForm, designerHourlyRate: e.target.value })}
                      placeholder="Used on proposal joinery"
                    />
                  </div>
                )}
                <div className="um-field">
                  <label>Company</label>
                  <input
                    type="text"
                    value={editForm.company}
                    onChange={e => setEditForm({ ...editForm, company: e.target.value })}
                    placeholder="Company name"
                  />
                </div>
              </div>
              <div className="um-field">
                <label>Phone</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
              <div className="um-field-row">
                <div className="um-field">
                  <label>Location</label>
                  <input
                    type="text"
                    value={editForm.location}
                    onChange={e => setEditForm({ ...editForm, location: e.target.value })}
                    placeholder="City, region"
                  />
                </div>
                <div className="um-field">
                  <label>Timezone</label>
                  <select
                    value={editForm.timezone}
                    onChange={e => setEditForm({ ...editForm, timezone: e.target.value })}
                  >
                    <option value="">Not set</option>
                    {timezones.map(tz => (
                      <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="um-field">
                <label>Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                  placeholder="Internal notes..."
                />
              </div>
            </div>
            <div className="um-modal-footer">
              <button className="um-btn-cancel" onClick={() => setEditingUser(null)}>Cancel</button>
              <button className="um-btn-save" onClick={saveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {awardTargetUser && (
        <GrantAchievementModal
          user={awardTargetUser}
          onClose={() => setAwardTargetUser(null)}
        />
      )}

      {hoursOpen && (
        <TeamActivityReportModal
          users={crmUsers.filter((u) => !isCrmTestUser(u) && !isCrmWorkstationUser(u))}
          onClose={() => setHoursOpen(false)}
        />
      )}

      {showInviteModal && (
        <UnifiedInviteModal onClose={() => setShowInviteModal(false)} />
      )}
      {showWorkstationModal && (
        <UnifiedInviteModal
          workstation
          defaultRole="staff"
          onClose={() => setShowWorkstationModal(false)}
        />
      )}

      {showCpaInvite && (
        <div className="um-modal-overlay" onClick={() => setShowCpaInvite(false)}>
          <div className="um-modal" onClick={e => e.stopPropagation()} role="dialog" aria-labelledby="um-cpa-invite-title">
            <div className="um-modal-header">
              <h3 id="um-cpa-invite-title">Invite CPA</h3>
              <button type="button" className="um-modal-close" onClick={() => setShowCpaInvite(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="um-modal-body">
              <p className="um-hours-help">
                Sends an email with a secure setup link (24h). They land in Finances only — comments allowed, numbers stay read-only.
              </p>
              <div className="um-field">
                <label htmlFor="um-cpa-invite-name">Full name</label>
                <input
                  id="um-cpa-invite-name"
                  type="text"
                  value={cpaInviteName}
                  onChange={e => setCpaInviteName(e.target.value)}
                  placeholder="e.g. Jordan Hale"
                  autoComplete="name"
                />
              </div>
              <div className="um-field">
                <label htmlFor="um-cpa-invite-email">Email</label>
                <input
                  id="um-cpa-invite-email"
                  type="email"
                  value={cpaInviteEmail}
                  onChange={e => setCpaInviteEmail(e.target.value)}
                  placeholder="bookkeeper@firm.com"
                  autoComplete="email"
                />
              </div>
              {cpaInviteResult && (
                <div className={`um-invite-result ${cpaInviteResult.success ? 'is-success' : 'is-error'}`}>
                  {cpaInviteResult.success ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
                  <div>
                    <p>{cpaInviteResult.message}</p>
                    {cpaInviteResult.setupLink && (
                      <div className="um-invite-creds">
                        <div><span>Portal</span> {cpaInviteResult.portalUrl}</div>
                        <div><span>Email</span> {cpaInviteResult.email}</div>
                        <div><span>Setup link</span> <code>{cpaInviteResult.setupLink}</code></div>
                        <button
                          type="button"
                          className="um-btn-secondary"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              `Sign in at: ${cpaInviteResult.portalUrl}\nEmail: ${cpaInviteResult.email}\nSetup link: ${cpaInviteResult.setupLink}`,
                            );
                          }}
                        >
                          <Copy size={12} /> Copy credentials
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="um-modal-footer">
              <button type="button" className="um-btn-cancel" onClick={() => setShowCpaInvite(false)}>Cancel</button>
              <button
                type="button"
                className="um-btn-save"
                onClick={handleSendCpaInvite}
                disabled={!cpaInviteName.trim() || !cpaInviteEmail.trim() || cpaInviteSending}
              >
                {cpaInviteSending ? 'Sending…' : <><Send size={13} /> Send invite</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default UserManagement;
